import {
  Chat,
  CheckCircle,
  ContentCopy,
  ErrorOutline,
  KeyboardArrowDown,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControlLabel,
  InputBase,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import planData from "../plans.json";
import { useLocation, useNavigate } from "react-router-dom";
import AddToSlackModal from "./AddUserToSlack";
import ChannelAddedModal from "./ChannelAddelModal";
import { jwtDecode } from "jwt-decode";
import generatecode from "../utils/generateCode";
import CryptoJS from "crypto-js";
import posthog from "posthog-js";
import forge from "node-forge";
import encryptApi from "../utils/encryptApi";
import decryptApi from "../utils/decryptApi";
import { Mail, Phone, X, ExternalLink } from "lucide-react";
import { CHANNEL_TYPES as channel_types } from "../../config/channelTypes";
import { isGoogleEmailForAddress, isLikelyValidEmail } from "../../utils/emailProvider";
import FrontChannelSettings from "./FrontChannelSettings";
import {
  canUseFrontVoiceMode,
  getDefaultFrontChannelFields,
  getFrontModeForPlan,
} from "../../utils/frontChannelSettings";
const AddChannelModal = ({
  open,
  setOpen,
  channels,
  setChannels,
  channelAddedModal,
  setOpenChannelAddedModal,
  channelPendingModal,
  setOpenChannelPendingModal,
  addToSlackModal,
  setOpenAddToSlackModal,
  setChannelId,
  /** When opening from route config: preselect channel type (e.g. "forward_number", "email") */
  initialChannelType,
  /** When a channel is added from route config: callback to auto-select the new channel (receives channel value) */
  callChannelAddedAndClear,
}) => {
  const location = useLocation();
  const [loader, setLoader] = useState(false);
  const [error_msg, setErrormsg] = useState(null);
  const [code_sent, setCodeSent] = useState(false);
  const [isGoogleEmail, setIsGoogleEmail] = useState(false);
  const [isCheckingProvider, setIsCheckingProvider] = useState(false);

  const [planDetails, setPlanDetails] = useState(null);
  const navigate = useNavigate();

  // Gmail search URL for emails from hello@twilanswer.com in the last 24 hours
  const gmailSearchUrl = `https://mail.google.com/mail/u/0/#search/from:hello@twilanswer.com+newer_than:1d`;

  const [code, setCode] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const searchParams = new URLSearchParams(location.search); // Parse the query string
  const [channel, setChannel] = useState({
    label: "",
    last_updated: String(Math.floor(Date.now() / 1000)),
    status: "active",
    type: initialChannelType ?? (searchParams.get("channeltype") || null),
    value: "",
    organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
    slack_id: "",
    api_key: "",
    front_id: "",
    ...getDefaultFrontChannelFields(),
  });

  // Debounced Google Workspace/Gmail detection for email channel
  useEffect(() => {
    if (channel?.type !== "email" || !channel.value) {
      setIsGoogleEmail(false);
      setIsCheckingProvider(false);
      return;
    }
    if (!isLikelyValidEmail(channel.value)) {
      setIsGoogleEmail(false);
      setIsCheckingProvider(false);
      return;
    }
    setIsCheckingProvider(true);
    const handle = setTimeout(async () => {
      try {
        const result = await isGoogleEmailForAddress(channel.value);
        setIsGoogleEmail(result);
      } catch {
        setIsGoogleEmail(false);
      } finally {
        setIsCheckingProvider(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [channel?.type, channel?.value]);

  //get the url param
  // Access the current location
  useEffect(() => {
    const param1 = searchParams.get("channeltype"); // Get value of 'param1'

    if (param1 && channel?.type.length >= 1) {
      setLoader(true);
      setOpen(true);

      // Remove parameters after 2 seconds
      setTimeout(() => {
        searchParams.delete("channeltype");
        navigate({
          pathname: location.pathname,
          search: searchParams.toString(), // Update the URL without the removed params
        });
      }, 0);
    }
  }, [location]);

  // When modal opens: show list unless opened with a preselected type from route config
  useEffect(() => {
    if (open) {
      if (initialChannelType) {
        setChannel((prev) => ({ ...prev, type: initialChannelType, value: "", label: "" }));
      } else {
        setChannel((prev) => ({ ...prev, type: null, value: "", label: "" }));
      }
    }
  }, [open, initialChannelType]);

  useEffect(() => {
    setCode(generatecode());
    setCodeSent(false);
    setChannel((prev) => ({ ...prev, value: "", label: "" }));
    setErrormsg(null);
  }, [channel?.type]);

  useEffect(() => {
    if (timeLeft === 0) {
      setCode(generatecode());
    }

    const intervalId = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(intervalId); // Cleanup on unmount or re-render
  }, [timeLeft]);

  const check_subscription = async () => {
    try {
      setLoader(true);
      let sub = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "stripe/check-subscription",
        {
          organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
          customer_id: JSON.parse(localStorage.getItem("organization"))
            .subscription_details.M.stripe_cust_id.S,
        }
      );

      setPlanDetails(planData[sub.data?.planDetails[0].plan_id]);
      setLoader(false);
      return;
    } catch (error) {
      // Log for production diagnostics when subscription check fails
      console.error("AddChannelModal: check subscription failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setErrormsg(error.response.data.message);
      }
      setLoader(false);
    }
  };
  useEffect(() => {
    check_subscription();
  }, []);
  function generateRandomAlphanumeric() {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const length = 16; // Length of the random string
    let result = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }
    return result;
  }
  useEffect(() => {
    if (channel?.type === "front") {
      let randomalphanumeric = generateRandomAlphanumeric();

      setChannel({
        ...channel,
        value: randomalphanumeric,
        front_id: randomalphanumeric,
        slack_id: randomalphanumeric,
        status: "pending",
        api_key: randomalphanumeric,
        ...getDefaultFrontChannelFields(),
      });
    }

    if (channel?.type === "slack") {
      setChannel({
        ...channel,
        value: "N/A",
        status: "pending",
        slack_id: "N/A",
        front_id: "N/A",
      });
    }
    if (channel?.type === "elevenlabs") {
      setChannel((prev) => ({ ...prev, value: "", status: "active" }));
      setElevenlabsAgents([]);
      setElevenlabsSelectedIds(new Set());
      setElevenlabsHasTwilanswerTools(null);
      setElevenlabsAddTools(true);
    }
  }, [channel?.type]);

  async function fetchElevenlabsAgents() {
    const apiKey = channel?.api_key?.trim();
    if (!apiKey) {
      setErrormsg("Please enter your ElevenLabs API Key");
      return;
    }
    setErrormsg(null);
    setElevenlabsFetching(true);
    setElevenlabsHasTwilanswerTools(null);
    try {
      const [agentsRes, toolsRes] = await Promise.all([
        axios.post(process.env.REACT_APP_BACKEND_URL + "elevenlabs/list-agents", {
          organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
          api_key: apiKey,
          page_size: 30,
          cursor: null,
          search: null,
          archived: false,
          show_only_owned_agents: false,
          sort_by: "name",
          sort_direction: "asc",
        }),
        axios.post(process.env.REACT_APP_BACKEND_URL + "elevenlabs/list-tools", { organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S, api_key: apiKey }).catch(() => ({ data: { tools: [] } })),
      ]);
      if (agentsRes.data?.status === "success" && Array.isArray(agentsRes.data?.agents)) {
        setElevenlabsAgents(agentsRes.data.agents);
        setElevenlabsSelectedIds(new Set(agentsRes.data.agents.map((a) => a.agent_id)));
      } else {
        setErrormsg(agentsRes.data?.message || "Failed to fetch agents");
      }
      const tools = toolsRes.data?.tools || [];
      const hasTwilanswer = tools.some(
        (t) => (t.tool_config?.name || t.name || "").toLowerCase().includes("twilanswer")
      );
      setElevenlabsHasTwilanswerTools(hasTwilanswer);
      setElevenlabsAddTools(!hasTwilanswer);
    } catch (error) {
      // Log for production diagnostics when ElevenLabs agents/tools fetch fails
      console.error("AddChannelModal: fetch ElevenLabs agents failed", error.response?.data || error.message || error);
      const backendMsg = error.response?.data?.message || error.message;
      const scopeHint = "Your API key must have the Agents scope with at least Read permission (Write is recommended for adding tools).";
      setErrormsg(backendMsg ? `${backendMsg} ${scopeHint}` : scopeHint);
      setElevenlabsAgents([]);
    } finally {
      setElevenlabsFetching(false);
    }
  }

  function toggleElevenlabsAgent(agentId) {
    setElevenlabsSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  }

  async function sendEmailVerificationLink() {
    setErrormsg(null);

    setLoader(true);
    //using node forge to encrypt data
    try {
      let response = await axios.post(
        process.env.REACT_APP_BACKEND_URL +
          "email/send-email-verification-code",
        {
          email: channel?.type === "email" && channel.value,
          toName: channel.label,
          code: await encryptApi(code),
        }
      );
      setCodeSent(true);
      setTimeLeft(60);

      setLoader(false);
    } catch (error) {
      // Log for production diagnostics when send email verification fails
      console.error("AddChannelModal: send email verification failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setErrormsg(error.response.data.message);
      }
      setLoader(false);
    }
  }

  function decodeToken(token) {
    try {
      const decoded = jwtDecode(token);
      return decoded.api_key;
    } catch (error) {
      // Log when channel token is invalid or malformed
      console.error("Invalid token:", error);
      return null;
    }
  }

  async function sendPhoneVerificationText() {
    setErrormsg(null);
    const ciphertext = CryptoJS.AES.encrypt(
      code,
      process.env.REACT_APP_TWILANSWER_SECRET
    ).toString();
    setLoader(true);
    try {
      let response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "twilio/read-num-aloud",
        {
          organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
          fromNumber: JSON.parse(localStorage.getItem("organization"))
            ?.twilio_integration?.M?.primary_phone?.S,
          forwarding_number: channel.value,
          code: await encryptApi(code),
        }
      );
      setCodeSent(true);
      setTimeLeft(60);

      setLoader(false);
    } catch (error) {
      // Log for production diagnostics when send phone verification (call) fails
      console.error("AddChannelModal: send phone verification call failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setErrormsg(error.response.data.error);
      }
      setLoader(false);
    }
  }

  async function sendPhoneVerificationSms() {
    setErrormsg(null);

    setLoader(true);
    try {
      let response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "twilio/send-text-message",
        {
          organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
          fromNumber: JSON.parse(localStorage.getItem("organization"))
            .twilio_integration?.M.primary_phone.S,
          toNumber: channel.value,
          code: await encryptApi(code),
        }
      );
      setCodeSent(true);
      setTimeLeft(60);

      setLoader(false);
    } catch (error) {
      // Log for production diagnostics when send phone verification (SMS) fails
      console.error("AddChannelModal: send phone verification SMS failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setErrormsg(error.response.data.error?.message ?? error.response.data.message);
      } else {
        setErrormsg(error.response?.message ?? "Request failed");
      }
      setLoader(false);
    }
  }

  const [successMsg, setSuccessMsg] = useState(null);

  /** Front: 1 = API key + copy to save, 2 = setup instructions before pending modal */
  const [frontSetupStep, setFrontSetupStep] = useState(1);
  const [savedFrontApiKey, setSavedFrontApiKey] = useState("");

  useEffect(() => {
    if (channel?.type !== "front") {
      setFrontSetupStep(1);
      setSavedFrontApiKey("");
    }
  }, [channel?.type]);

  useEffect(() => {
    if (!open) {
      setFrontSetupStep(1);
      setSavedFrontApiKey("");
    }
  }, [open]);

  // ElevenLabs: agents from API and which are enabled
  const [elevenlabsAgents, setElevenlabsAgents] = useState([]);
  const [elevenlabsFetching, setElevenlabsFetching] = useState(false);
  const [elevenlabsSelectedIds, setElevenlabsSelectedIds] = useState(new Set());
  // ElevenLabs: Twilanswer tools - null = unknown, true = already exist
  const [elevenlabsHasTwilanswerTools, setElevenlabsHasTwilanswerTools] = useState(null);
  const [elevenlabsAddTools, setElevenlabsAddTools] = useState(true);

  useEffect(() => {
    setChannel({
      ...channel,
      organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
    });
  }, [error_msg]);

  const addChannel = async () => {
    setErrormsg(null);

    let check = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
      {
        userId: JSON.parse(localStorage.getItem("user")).userId.S,
      }
    );

    if (check.status === 200) {
      const user___ = check.data.user;

      if (
        user___.relatedOrgs.L?.find(
          (e) =>
            e?.M.org_id?.S ===
            JSON.parse(localStorage.getItem("organization")).orgId.S
        )
      ) {
        if (channel.label?.length >= 1) {
          const elevenlabsValue = channel?.type === "elevenlabs" ? JSON.stringify([...elevenlabsSelectedIds].sort()) : channel.value;
          if (
            JSON.parse(localStorage.getItem("organization")).channels?.L.find(
              (e) =>
                e.M.type.S === channel?.type &&
                e.M.value.S === elevenlabsValue &&
                e.M.value.S !== "N/A"
            )
          ) {
            setErrormsg(
              "Another channel with " + channel?.type + " already exist"
            );
          } else {
            if (
              channel?.type === "front" ||
              channel?.type === "slack" ||
              channel?.type === "elevenlabs" ||
              channel?.code === code
            ) {
              try {
                setLoader(true);
                if (channel?.type === "elevenlabs" && elevenlabsAddTools) {
                  try {
                    await axios.post(process.env.REACT_APP_BACKEND_URL + "elevenlabs/create-tools", {
                      organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
                      api_key: channel.api_key || "",
                    });
                  } catch (createToolsError) {
                    // Log for production diagnostics when ElevenLabs create-tools fails
                    console.error("AddChannelModal: create ElevenLabs tools failed", createToolsError.response?.data || createToolsError.message || createToolsError);
                    setErrormsg(
                      "Failed to create tools. The API key must have Read/Write permissions for agents to work properly."
                    );
                    setLoader(false);
                    return;
                  }
                }
                const payload = channel?.type === "elevenlabs"
                  ? (() => {
                      const agents = elevenlabsAgents
                        .filter((a) => elevenlabsSelectedIds.has(a.agent_id))
                        .map((a) => ({ name: a.name, id: a.agent_id }));
                      const el_exclusion_list = elevenlabsAgents
                        .filter((a) => !elevenlabsSelectedIds.has(a.agent_id))
                        .map((a) => a.agent_id);
                      return {
                        ...channel,
                        value: JSON.stringify([...elevenlabsSelectedIds].sort()),
                        api_key: channel.api_key || "",
                        agents,
                        el_exclusion_list,
                      };
                    })()
                  : channel?.type === "front"
                    ? {
                        ...channel,
                        frontMode: getFrontModeForPlan(channel.frontMode, planDetails),
                      }
                  : channel;
                const payloadWithOrg = { ...payload, organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S };
                let channel_data = await axios.post(
                  process.env.REACT_APP_BACKEND_URL + "add-channel",
                  payloadWithOrg
                );

                localStorage.setItem(
                  "organization",
                  JSON.stringify(channel_data.data.updatedItem)
                );

                setChannels(channel_data.data.updatedItem.channels.L);
                setChannelId(channel_data.data.channel_id);
                if (callChannelAddedAndClear) {
                  const channelId = channel_data.data.channel_id || "";
                  const phoneForForward = channel?.type === "forward_number" ? channel.value : undefined;
                  callChannelAddedAndClear(phoneForForward != null ? [channelId, phoneForForward] : channelId);
                }

                if (channel?.type === "slack") {
                  setOpenAddToSlackModal(true);
                }

                if (channel?.type === "front") {
                  setSavedFrontApiKey(channel.value);
                  setFrontSetupStep(2);
                  setCopied(false);
                  setElevenlabsAgents([]);
                  setElevenlabsSelectedIds(new Set());
                  setElevenlabsHasTwilanswerTools(null);
                  setElevenlabsAddTools(true);
                  setCodeSent(false);
                  setSuccessMsg("Channel successfully added");
                  posthog.capture("Added a channel", {
                    email: JSON.parse(localStorage.getItem("user")).email.S,
                    userId: JSON.parse(localStorage.getItem("user")).userId.S,
                  });
                  setTimeout(() => {
                    setSuccessMsg(null);
                  }, 3000);
                  setLoader(false);
                  setErrormsg(null);
                  setCodeSent(null);
                  setTimeLeft(0);
                  return;
                }

                if (channel?.type === "email") {
                  setOpenChannelAddedModal(true);
                }

                if (channel?.type === "forward_number") {
                  setOpenChannelAddedModal(true);
                }

                if (channel?.type === "sms") {
                  setOpenChannelAddedModal(true);
                }
                if (channel?.type === "elevenlabs") {
                  setOpenChannelAddedModal(true);
                }
                setChannel({
                  label: "",
                  last_updated: String(Math.floor(Date.now() / 1000)),
                  status: "active",
                  type: searchParams.get("channeltype") || "email",
                  value: "",
                  organization_id: JSON.parse(
                    localStorage.getItem("organization")
                  ).orgId.S,
                  slack_id: "",
                  api_key: "",
                  front_id: "",
                });
                setElevenlabsAgents([]);
                setElevenlabsSelectedIds(new Set());
                setElevenlabsHasTwilanswerTools(null);
                setElevenlabsAddTools(true);

                setCodeSent(false);

                setSuccessMsg("Channel successfully added");
                //post hog track event for add a channel
                posthog.capture("Added a channel", {
                  email: JSON.parse(localStorage.getItem("user")).email.S,
                  userId: JSON.parse(localStorage.getItem("user")).userId.S,
                });

                setTimeout(() => {
                  setSuccessMsg(null);
                }, 3000);
                setOpen(false);

                setLoader(false);
                setErrormsg(null);
                setCodeSent(null);
                setTimeLeft(0);
              } catch (error) {
                // Log for production diagnostics when add channel fails
                console.error("AddChannelModal: add channel failed", error.response?.data || error.message || error);
                if (error.response && error.response.data) {
                  setErrormsg(error.response.data.message);
                }
                setLoader(false);
              }
            } else {
              setErrormsg("verification code does not match");
            }
          }
        } else {
          setErrormsg("Please add channel name");
        }
      } else {
        window.location.reload();
      }
    }
  };

  const [copied, setCopied] = useState(false);

  //copy to clip board
  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      // Log when clipboard API fails (e.g. permissions)
      console.error("Failed to copy text:", err);
    }
  }

  async function handleFrontCopyAndSave() {
    setErrormsg(null);
    if (!channel.label?.trim()?.length) {
      setErrormsg("Please add channel name");
      return;
    }
    try {
      await navigator.clipboard.writeText(channel.value);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
    await addChannel();
  }

  function finishFrontSetup() {
    setOpenChannelPendingModal(true);
    setOpen(false);
    setFrontSetupStep(1);
    setSavedFrontApiKey("");
    setChannel({
      label: "",
      last_updated: String(Math.floor(Date.now() / 1000)),
      status: "active",
      type: searchParams.get("channeltype") || "email",
      value: "",
      organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
      slack_id: "",
      api_key: "",
      front_id: "",
    });
    setCodeSent(false);
    setErrormsg(null);
  }

  return (
    <Dialog
      open={open}
      fullWidth={false}
      maxWidth={"sm"}
      BackdropProps={{
        style: { backgroundColor: "rgba(142, 149, 159, 0.75)" }, // Adjust opacity here
      }}
    >
      {channel?.type ? (
        <div className="px-8 py-6">
          <div className="flex items-center justify-between space-x-4 mb-4">
            <div className="text-[17px] font-semibold text-black  ">
              {channel?.type === "front" && frontSetupStep === 2
                ? "Connect Front"
                : "Configure Channel"}
            </div>
            <X onClick={() => setOpen(false)} />
          </div>
          {error_msg && (
            <div className=" flex items-center text-red-500 lg:text-[13px] text-[12px] mt-2  font-semibold ">
              <ErrorOutline sx={{ fontSize: "", mr: 1 }} /> {error_msg}
            </div>
          )}

          {successMsg && (
            <Alert color="success">
              <div className=" lg:text-[13px] text-[12px]   font-semibold ">
                {successMsg}
              </div>
            </Alert>
          )}

          {channel?.type === "front" && frontSetupStep === 2 ? (
            <div className="mt-4 space-y-5">
              <p className="lg:text-[14px] text-[13px] text-gray-800">
                Your channel is saved. Finish connecting it in Front:
              </p>
              <ol className="list-decimal pl-5 space-y-2 lg:text-[14px] text-[13px] text-gray-800">
                <li>
                  In Front, open the Twilanswer integration (or connector) for
                  your inbox.
                </li>
                <li>
                  When Front asks for an API key, paste the Twilanswer key
                  below—you can copy it again from here if needed.
                </li>
                <li>
                  Complete the setup in Front. This channel stays pending until
                  Front confirms the integration.
                </li>
              </ol>
              <a
                href="https://twilanswer.com/docs/how-to-set-up-the-front-integration-in-twilanswer/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-brand-primary font-semibold lg:text-[14px] text-[13px] hover:underline"
              >
                Full setup guide
                <ExternalLink className="w-4 h-4 shrink-0" strokeWidth={2} />
              </a>
              <div>
                <div className="lg:text-[14px] text-[13px] font-semibold mb-2">
                  Twilanswer API key
                </div>
                <div className="flex items-stretch gap-3">
                  <div className="flex-1 min-w-0">
                    <InputBase
                      readOnly
                      value={savedFrontApiKey}
                      sx={{
                        bgcolor: "#F9FAFB",
                        border: "1px solid #D1D5DB",
                        width: "100%",
                        px: 2,
                        py: "5px",
                        borderRadius: "8px",
                        fontSize: { lg: "16px", xs: "14px" },
                      }}
                    />
                  </div>
                  <Button
                    sx={{
                      textTransform: "none",
                      border: "1px solid #E6E7EB",
                      color: "black",
                      bgcolor: "white",
                      py: "7px",
                      px: "16px",
                      borderRadius: "6px",
                      fontSize: { lg: "14px", xs: "12px" },
                      flexShrink: 0,
                      alignSelf: "stretch",
                      display: "inline-flex",
                      alignItems: "center",
                      "&:hover": {
                        bgcolor: "#F9FAFB",
                        borderColor: "#D1D5DB",
                      },
                    }}
                    onClick={() => copyToClipboard(savedFrontApiKey)}
                  >
                    {copied ? (
                      <CheckCircle
                        sx={{
                          color: "gray",
                          width: 20,
                          height: 20,
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <ContentCopy
                        sx={{
                          color: "gray",
                          width: 20,
                          height: 20,
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
          <div>
            <div className="lg:text-[14px] text-[13px] font-semibold">
              Channel Type{" "}
            </div>

            <div className=" flex items-center space-x-2  border-[#E6E7EB] rounded-[10px] lg:pl-8 pl-2 lg:pr-20 py-3 border-[2px] bg-[#F9FAFB] mb-2">
              {channel_types.find((item) => item.type == channel.type).icon}
              <div>
                <div className="font-semibold lg:text-[16px] text-[14px] flex items-center gap-2">
                  {channel_types.find((item) => item.type == channel.type).name}
                  {channel_types.find((item) => item.type == channel.type).beta && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
                      Beta
                    </span>
                  )}
                </div>
                <div className="text-[13px]">
                  {
                    channel_types.find((item) => item.type == channel.type)
                      .description
                  }
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className="lg:text-[14px] text-[13px] font-semibold flex items-center mb-2">
              Channel Nickname{" "}
            </div>
            <div className="">
              <InputBase
                value={channel.label}
                onChange={(e) => {
                  setChannel({ ...channel, label: e.target.value });
                }}
                placeholder="Enter a nickname for this channel"
                sx={{
                  bgcolor: "white",
                  border: "1px solid #D1D5DB",
                  width: "100%",
                  px: 2,
                  py: "5px",
                  borderRadius: "8px",
                  fontSize: { lg: "16px", xs: "14px" },
                }}
              />
            </div>
          </div>

          {channel?.type === "forward_number" && (
            <>
              <div className="">
                <div className="lg:text-[14px] text-[13px] font-semibold flex items-center mb-2">
                  Phone Number{" "}
                </div>

                <div className="flex item-center">
                  <div className="flex-1 mr-2">
                    <InputBase
                      value={channel.value}
                      onChange={(e) => {
                        setChannel({ ...channel, value: e.target.value });
                      }}
                      placeholder="Enter phone number"
                      sx={{
                        bgcolor: "white",
                        border: "1px solid #D1D5DB",
                        width: "100%",
                        px: 2,
                        py: "5px",
                        borderRadius: "8px",
                        fontSize: { lg: "16px", xs: "14px" },
                      }}
                    />
                  </div>

                  <div className="flex lg:justify-center mt-2 lg:mt-0 relative  ">
                    {timeLeft >= 1 ? (
                      <Button
                        variant="contained"
                          color="primary"
                          sx={{
                          h: "100%",
                          px: "10px",
                          borderRadius: "7px",
                          fontSize: { lg: "14px", xs: "12px" },
                        }}
                      >
                        Expires {timeLeft}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                          color="primary"
                          sx={{
                          h: "100%",
                          px: "1px",
                          borderRadius: "7px",
                          fontSize: { lg: "14px", xs: "12px" },
                        }}
                        onClick={sendPhoneVerificationText}

                      >
                        Verify
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {code_sent && (
                <div className=" ">
                  <div className="lg:text-[14px] text-[13px] font-semibold flex items-center mt-2">
                    Verification Code
                  </div>

                  <div className="">
                    <InputBase
                      value={channel.code}
                      onChange={(e) => {
                        setChannel({ ...channel, code: e.target.value });
                      }}
                      placeholder="enter verification code"
                      sx={{
                        bgcolor: "white",
                        border: "1px solid #D1D5DB",
                        width: "100%",
                        px: 2,
                        py: "5px",
                        borderRadius: "8px",
                        fontSize: { lg: "16px", xs: "14px" },
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {channel?.type === "email" && (
            <>
              <div className="w-full">
                <div className="lg:text-[14px] text-[13px] font-semibold mb-2 w-full">
                  Email Address
                </div>

                <div className="flex items-center space-x-2">
                  <div className="flex-1">
                    <InputBase
                      value={channel.value}
                      onChange={(e) => {
                        setChannel({ ...channel, value: e.target.value });
                      }}
                      placeholder="Enter email address"
                      sx={{
                        bgcolor: "white",
                        border: "1px solid #D1D5DB",
                        width: "100%",
                        px: 2,
                        py: "5px",
                        borderRadius: "8px",
                        fontSize: { lg: "16px", xs: "14px" },
                      }}
                    />
                  </div>

                  <div className="flex lg:justify-center mt-2 lg:mt-0 relative  ">
                    {timeLeft >= 1 ? (
                      <Button
                        variant="contained"
                          color="primary"
                          sx={{
                          px: "10px",
                          borderRadius: "7px",
                          fontSize: { lg: "14px", xs: "12px" },
                          h: "100%",
                        }}
                      >
                        Expires {timeLeft}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                          color="primary"
                          sx={{
                          px: "1px",
                          borderRadius: "7px",
                          fontSize: { lg: "14px", xs: "12px" },
                          h: "100%",
                        }}
                        onClick={sendEmailVerificationLink}
                        disabled={!channel.value}
                      >
                        Verify
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              {code_sent && (
                <>
                  <div className="">
                    <div className="lg:text-[14px] text-[13px] font-semibold flex items-center mb-2 mt-2">
                      Verification Code
                    </div>

                    <div className="">
                      <InputBase
                        value={channel.code}
                        onChange={(e) => {
                          setChannel({ ...channel, code: e.target.value });
                        }}
                        placeholder="enter verification code"
                        sx={{
                          bgcolor: "white",
                          border: "1px solid #D1D5DB",
                          width: "100%",
                          px: 2,
                          py: "5px",
                          borderRadius: "8px",
                          fontSize: { lg: "16px", xs: "14px" },
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Open Gmail button (only for Google emails) */}
                  {isGoogleEmail && (
                    <div className="mt-3">
                      <Button
                        fullWidth
                        onClick={() => {
                          window.open(gmailSearchUrl, "_blank", "noopener,noreferrer");
                        }}
                        startIcon={<ExternalLink size={16} />}
                        sx={{
                          textTransform: "none",
                          backgroundColor: "#16a34a",
                          color: "#fff",
                          borderRadius: "10px",
                          py: "8px",
                          fontSize: { lg: "14px", xs: "13px" },
                          "&:hover": {
                            backgroundColor: "#15803d",
                          },
                        }}
                      >
                        Open Gmail
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {channel?.type === "sms" && (
            <>
              <div className="">
                <div className="lg:text-[14px] text-[13px] font-semibold flex items-center">
                  Phone Number
                </div>

                <div className="flex items-center space-x-2">
                  <div className="flex-1">
                    <InputBase
                      value={channel.value}
                      onChange={(e) => {
                        setChannel({ ...channel, value: e.target.value });
                      }}
                      placeholder="Enter phone number"
                      sx={{
                        bgcolor: "white",
                        border: "1px solid #D1D5DB",
                        width: "100%",
                        px: 2,
                        py: "5px",
                        borderRadius: "8px",
                        fontSize: { lg: "16px", xs: "14px" },
                      }}
                    />
                  </div>

                  <div className="flex lg:justify-center mt-2 lg:mt-0 relative  ">
                    {timeLeft >= 1 ? (
                      <Button
                        variant="contained"
                          color="primary"
                          sx={{
                          h: "100%",
                          px: "10px",
                          borderRadius: "7px",
                          fontSize: { lg: "14px", xs: "12px" },
                        }}
                      >
                        Expires {timeLeft}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                          color="primary"
                          sx={{
                          h: "100%",
                          px: "1px",
                          borderRadius: "7px",
                          fontSize: { lg: "14px", xs: "12px" },
                        }}
                        onClick={sendPhoneVerificationSms}
                      >
                        Verify
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {code_sent && (
                <div className="">
                  <div className="lg:text-[14px] text-[13px] font-semibold flex items-center mb-2 mt-2 ">
                    Verification Code
                  </div>

                  <div className="">
                    <InputBase
                      value={channel.code}
                      onChange={(e) => {
                        setChannel({ ...channel, code: e.target.value });
                      }}
                      placeholder="enter verification code"
                      sx={{
                        bgcolor: "white",
                        border: "1px solid #D1D5DB",
                        width: "100%",
                        px: 2,
                        py: "5px",
                        borderRadius: "8px",
                        fontSize: { lg: "16px", xs: "14px" },
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {channel?.type === "front" && frontSetupStep === 1 && (
            <>
              {planDetails?.planName !== "Basic" ? (
                <>
                <div className="mb-5">
                  <FrontChannelSettings
                    frontMode={channel.frontMode}
                    canUseVoiceMode={canUseFrontVoiceMode(planDetails)}
                    planLoaded={planDetails != null}
                    onUpgrade={() => navigate("/dashboard/settings/billing")}
                    onChange={({ frontMode }) =>
                      setChannel({ ...channel, frontMode })
                    }
                  />
                </div>
                <div>
                  <div className="">
                    <div className="lg:text-[14px] text-[13px] font-semibold flex items-center">
                      Front API key
                    </div>

                    <div className="flex items-stretch gap-3">
                      <div className="flex-1 min-w-0">
                        <InputBase
                          readOnly
                          value={channel.value}
                          sx={{
                            bgcolor: "#F9FAFB",
                            border: "1px solid #D1D5DB",
                            width: "100%",
                            px: 2,
                            py: "5px",
                            borderRadius: "8px",
                            fontSize: { lg: "16px", xs: "14px" },
                          }}
                        />
                      </div>

                      <Button
                        sx={{
                          textTransform: "none",
                          border: "1px solid #E6E7EB",
                          color: "black",
                          bgcolor: "white",
                          py: "7px",
                          px: "16px",
                          borderRadius: "6px",
                          fontSize: { lg: "14px", xs: "12px" },
                          flexShrink: 0,
                          alignSelf: "stretch",
                          display: "inline-flex",
                          alignItems: "center",
                          "&:hover": {
                            bgcolor: "#F9FAFB",
                            borderColor: "#D1D5DB",
                          },
                        }}
                        disabled={loader}
                        onClick={handleFrontCopyAndSave}
                      >
                        {loader ? (
                          <CircularProgress
                            size="1.1rem"
                            color="primary"
                          />
                        ) : copied ? (
                          <CheckCircle
                            sx={{
                              color: "gray",
                              width: 20,
                              height: 20,
                              mr: 1,
                              flexShrink: 0,
                            }}
                          />
                        ) : (
                          <ContentCopy
                            sx={{
                              color: "gray",
                              width: 20,
                              height: 20,
                              mr: 1,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        {!loader && (copied ? "Copied" : "Copy")}
                      </Button>
                    </div>
                    <div className="text-[12px] mt-1 text-gray-600">
                      Click Copy to copy this key and save the channel—you can
                      copy it again on the next step. Or use Save if you prefer
                      not to use the clipboard.
                    </div>
                  </div>
                </div>
                </>
              ) : (
                <Alert color="warning">
                  <div
                    className="text-[14px] hover:underline hover:text-blue-600 cursor-pointer "
                    onClick={() => navigate("/dashboard/settings/billing")}
                  >
                    {" "}
                    Upgrade to the Standard Plan to Connect Front{" "}
                  </div>
                </Alert>
              )}
            </>
          )}

          {channel?.type === "slack" && (
            <>
              {planDetails?.planName !== "Basic" ? (
                <div></div>
              ) : (
                <Alert color="warning">
                  <div
                    className="text-[14px] hover:underline hover:text-blue-600 cursor-pointer "
                    onClick={() => navigate("/dashboard/settings/billing")}
                  >
                    {" "}
                    Upgrade to the Standard Plan to Connect Slack{" "}
                  </div>
                </Alert>
              )}
            </>
          )}

          {channel?.type === "elevenlabs" && (
            <>
              <div className="mb-4">
                <div className="lg:text-[14px] text-[13px] font-semibold flex items-center mb-2">
                  ElevenLabs API Key
                </div>
                <InputBase
                  type="password"
                  value={channel.api_key || ""}
                  onChange={(e) => setChannel({ ...channel, api_key: e.target.value })}
                  placeholder="Enter your ElevenLabs API Key"
                  sx={{
                    bgcolor: "white",
                    border: "1px solid #D1D5DB",
                    width: "100%",
                    px: 2,
                    py: "5px",
                    borderRadius: "8px",
                    fontSize: { lg: "16px", xs: "14px" },
                  }}
                />
              </div>
              <div className="mb-4">
                <Button
                  variant="contained"
                    color="primary"
                    sx={{
                    py: "7px",
                    px: "20px",
                    borderRadius: "7px",
                    fontSize: { lg: "14px", xs: "12px" },
                  }}
                  onClick={fetchElevenlabsAgents}
                  disabled={elevenlabsFetching || !channel.api_key?.trim()}
                >
                  {elevenlabsFetching ? (
                    <CircularProgress size="1.3rem" sx={{ color: "white" }} />
                  ) : (
                    "Fetch Agents"
                  )}
                </Button>
              </div>
              {elevenlabsAgents.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="lg:text-[14px] text-[13px] font-semibold mb-0.5">
                        Add Twilanswer Tools
                      </div>
                      <div className="text-[12px] text-gray-500">
                        {elevenlabsHasTwilanswerTools === true
                          ? "Twilanswer tools already exist in your account. You can leave this off."
                          : elevenlabsHasTwilanswerTools === false
                          ? "Recommended: Add DTMF handler and hangup tools to your ElevenLabs account for better call control."
                          : "Add Twilanswer-specific tools (DTMF handler, hangup) to your ElevenLabs account."}
                      </div>
                    </div>
                    <Switch
                      color="primary"
                      checked={elevenlabsAddTools}
                      onChange={(e) => setElevenlabsAddTools(e.target.checked)}
                      size="small"
                    />
                  </div>
                </div>
              )}
              {elevenlabsAgents.length > 0 && (
                <div className="mb-4">
                  <div className="lg:text-[14px] text-[13px] font-semibold mb-2">
                    Select agents to make available
                  </div>
                  <Box
                    sx={{
                      maxHeight: 240,
                      overflowY: "auto",
                      border: "1px solid #D1D5DB",
                      borderRadius: "8px",
                      p: 1,
                      bgcolor: "#F9FAFB",
                    }}
                  >
                    {elevenlabsAgents.map((agent) => (
                      <FormControlLabel
                        key={agent.agent_id}
                        control={
                          <Checkbox
                            checked={elevenlabsSelectedIds.has(agent.agent_id)}
                            onChange={() => toggleElevenlabsAgent(agent.agent_id)}
                            size="small"
                          />
                        }
                        label={
                          <span className="text-[14px]">
                            {agent.name}
                            {agent.tags?.length > 0 && (
                              <span className="text-[12px] text-gray-500 ml-1">
                                ({agent.tags.join(", ")})
                              </span>
                            )}
                          </span>
                        }
                        sx={{ display: "block", m: 0, mb: 0.5 }}
                      />
                    ))}
                  </Box>
                </div>
              )}
            </>
          )}

            </>
          )}

          <div className="flex items-center justify-between space-x-4 mt-5">
                <div>
                  {!(channel?.type === "front" && frontSetupStep === 2) && (
                  <Button
                    sx={{
                      textTransform: "none",
                      border: "1px solid #E6E7EB",
                      color: "black",
                      py: "7px",
                      px: "20px",
                      borderRadius: "6px",
                      fontSize: { lg: "14px", xs: "12px" },
                    }}
                    onClick={() => {
                      setChannel({
                        label: "",
                        last_updated: String(Math.floor(Date.now() / 1000)),
                        status: "active",
                        type: searchParams.get("channeltype") || null,
                        value: "",
                        organization_id: JSON.parse(
                          localStorage.getItem("organization")
                        ).orgId.S,
                        slack_id: "",
                        api_key: "",
                        front_id: "",
                      });
                    }}
                  >
                    Back
                  </Button>
                  )}
                </div>

                <div className="flex  space-x-2">
                  <Button
                    sx={{
                      textTransform: "none",
                      border: "1px solid #E6E7EB",
                      color: "black",
                      py: "7px",
                      px: "20px",
                      borderRadius: "6px",
                      fontSize: { lg: "14px", xs: "12px" },
                    }}
                    onClick={() => {
                      setOpen(false);
                      setChannel({
                        type: "email",
                      });

                      setCodeSent(false);
                      setErrormsg(null);
                    }}
                  >
                    Cancel
                  </Button>

                  {channel?.type === "front" && frontSetupStep === 1 && (
                    <>
                      {planDetails?.planName !== "Basic" ? (
                        <Button
                          variant="contained"
                            color="primary"
                            sx={{
                            py: "4px",
                            px: "40px",
                            borderRadius: "7px",
                            fontSize: { lg: "14px", xs: "12px" },
                          }}
                          onClick={addChannel}
                          disabled={loader}
                        >
                          {loader ? (
                            <CircularProgress
                              size={"1.3rem"}
                              sx={{ color: "white" }}
                            />
                          ) : (
                            " Save"
                          )}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                            color="primary"
                            sx={{
                            py: "4px",
                            px: "10px",
                            borderRadius: "7px",
                            fontSize: { lg: "14px", xs: "12px" },
                          }}
                          onClick={() =>
                            navigate("/dashboard/settings/billing")
                          }
                          disabled={loader}
                        >
                          {loader ? (
                            <CircularProgress
                              size={"1.3rem"}
                              sx={{ color: "white" }}
                            />
                          ) : (
                            " Upgrade Plan"
                          )}
                        </Button>
                      )}
                    </>
                  )}

                  {channel?.type === "front" && frontSetupStep === 2 && (
                    <Button
                      variant="contained"
                        color="primary"
                        sx={{
                        py: "4px",
                        px: "40px",
                        borderRadius: "7px",
                        fontSize: { lg: "14px", xs: "12px" },
                      }}
                      onClick={finishFrontSetup}
                    >
                      Continue
                    </Button>
                  )}

                  {channel?.type === "slack" && (
                    <>
                      {planDetails?.planName !== "Basic" ? (
                        <Button
                          variant="contained"
                            color="primary"
                            sx={{
                            py: "4px",
                            px: "40px",
                            borderRadius: "7px",
                            fontSize: { lg: "14px", xs: "12px" },
                          }}
                          onClick={addChannel}
                          disabled={loader}
                        >
                          {loader ? (
                            <CircularProgress
                              size={"1.3rem"}
                              sx={{ color: "white" }}
                            />
                          ) : (
                            " Next"
                          )}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                            color="primary"
                            sx={{
                            py: "4px",
                            px: "10px",
                            borderRadius: "7px",
                            fontSize: { lg: "14px", xs: "12px" },
                          }}
                          onClick={() =>
                            navigate("/dashboard/settings/billing")
                          }
                          disabled={loader}
                        >
                          {loader ? (
                            <CircularProgress
                              size={"1.3rem"}
                              sx={{ color: "white" }}
                            />
                          ) : (
                            " Upgrade Plan"
                          )}
                        </Button>
                      )}
                    </>
                  )}

                  {channel?.type === "forward_number" && (
                    <>
                      {channels.filter((e) => e.M.type.S === "forward_number")
                        .length >= planDetails?.forwarding_numbers ? (
                        <Button
                          variant="contained"
                            color="primary"
                            sx={{
                            py: "4px",
                            px: "10px",
                            borderRadius: "7px",
                            fontSize: { lg: "14px", xs: "12px" },
                          }}
                          disabled={loader}
                          onClick={() =>
                            navigate("/dashboard/settings/billing")
                          }
                        >
                          {loader ? (
                            <CircularProgress
                              size={"1.3rem"}
                              sx={{ color: "white" }}
                            />
                          ) : (
                            " Upgrade Plan"
                          )}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                            color="primary"
                            sx={{
                            py: "4px",
                            px: "40px",
                            borderRadius: "7px",
                            fontSize: { lg: "14px", xs: "12px" },
                          }}
                          onClick={addChannel}
                          disabled={loader}
                        >
                          {loader ? (
                            <CircularProgress
                              size={"1.3rem"}
                              sx={{ color: "white" }}
                            />
                          ) : (
                            " Add Channel"
                          )}
                        </Button>
                      )}
                    </>
                  )}

                  {channel?.type === "sms" && (
                    <>
                      <Button
                        variant="contained"
                          color="primary"
                          sx={{
                          py: "4px",
                          px: "40px",
                          borderRadius: "7px",
                          fontSize: { lg: "14px", xs: "12px" },
                         
                        }}
                        onClick={addChannel}
                        disabled={loader}
                      >
                        {loader ? (
                          <CircularProgress
                            size={"1.3rem"}
                            sx={{ color: "white" }}
                          />
                        ) : (
                          " Save"
                        )}
                      </Button>
                    </>
                  )}

                  {channel?.type === "email" && (
                    <>
                      <Button
                        variant="contained"
                          color="primary"
                          sx={{
                          py: "4px",
                          px: "40px",
                          borderRadius: "7px",
                          fontSize: { lg: "14px", xs: "12px" },
                        }}
                        onClick={addChannel}
                        disabled={loader}
                      >
                        {loader ? (
                          <CircularProgress
                            size={"1.3rem"}
                            sx={{ color: "white" }}
                          />
                        ) : (
                          " Save"
                        )}
                      </Button>
                    </>
                  )}

                  {channel?.type === "elevenlabs" && (
                    <>
                      <Button
                        variant="contained"
                          color="primary"
                          sx={{
                          py: "4px",
                          px: "40px",
                          borderRadius: "7px",
                          fontSize: { lg: "14px", xs: "12px" },
                        }}
                        onClick={addChannel}
                        disabled={loader || !channel.api_key?.trim() || elevenlabsSelectedIds.size === 0}
                      >
                        {loader ? (
                          <CircularProgress
                            size={"1.3rem"}
                            sx={{ color: "white" }}
                          />
                        ) : (
                          " Save"
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>



         
        </div>
      ) : (
        <div>
          <div className="px-8 py-6">
            <div className="flex items-center justify-between space-x-4 mb-4">
              <div className="text-[17px] font-semibold text-black  ">
                {" "}
                Select Channel Type{" "}
              </div>
              <X onClick={() => setOpen(false)} className="text-[#dddada]" />
            </div>

            <div className="text-[13px] font-semibold text-[#4B5563] mb-2 ">
              {" "}
              Choose the type of channel you want to add:
            </div>

            <div>
              {channel_types.map((item) => (
                <div
                  key={item.type}
                  className=" flex items-center cursor-pointer space-x-2 hover:border-[#A5B4FC] border-[#E6E7EB] rounded-[10px] pl-4 pr-20 py-3 border-[2px] hover:bg-[#EEF2FF] mb-4"
                  onClick={() => {
                    setChannel({ ...channel, type: item.type });
                  }}
                >
                  {item.icon}
                  <div>
                    <div className="font-semibold text-[16px] flex items-center gap-2">
                      {item.name}
                      {item.beta && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
                          Beta
                        </span>
                      )}
                    </div>
                    <div className="text-[13px]">{item.description}</div>
                  </div>
                </div>
              ))}

              <Button
                sx={{
                  textTransform: "none",
                  border: "1px solid #E6E7EB",
                  color: "black",
                  py: "7px",
                  px: "20px",
                  borderRadius: "6px",
                  fontSize: { lg: "14px", xs: "12px" },
                  mt: 4
                }}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default AddChannelModal;
